// src/interactiveUGC/components/InteractiveChapterPreview.jsx
import * as React from "react";
import {
    Box, Typography, Button, IconButton, CircularProgress, Chip, Stack
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useTheme } from "@mui/material/styles";
import { t } from "@lingui/macro";
import {
    getInteractiveStoryChapter,
    getCharacters,
} from "../../utils/apis/interactiveStoriesAPI";

import {
    ensureAssetsManifest,
    getManifest,
    resolveBackgroundUrlByIndex,
} from "../../utils/assetsManifest";

/* ────────────────────────────── tiny image utils ───────────────────────────── */
const imgCache = new Map();
const loadImg = (url) =>
    new Promise((resolve, reject) => {
        if (!url) return resolve(null);
        if (imgCache.has(url)) return resolve(imgCache.get(url));
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
            imgCache.set(url, img);
            resolve(img);
        };
        img.onerror = reject;
        img.src = url;
    });

const asHostingUrl = (p) =>
    !p ? "" : /^https?:\/\//i.test(p) ? p : (p.startsWith("/") ? p : "/" + p);

/* Character composite order (same convention as CharacterList) */
const PART_ORDER = (g, fullbodyChosen) =>
    fullbodyChosen
        ? ["template", "fullbody", "eyes", "nose", "lips", "hair"]
        : ["template", "bottom", "top", "eyes", "nose", "lips", "hair"];

const CHAR_W = 816;
const CHAR_H = 1456;

function characterLayerUrls(character) {
    try {
        const m = getManifest();
        const g = character?.gender || "female";

        const urlForPart = (part, idx) => {
            const list = m?.characters?.[g]?.[part] || [];
            const rel = list[idx] || "";
            return asHostingUrl(rel);
        };

        const fullbodyChosen =
            character?.fullbody !== null && character?.fullbody !== undefined;

        // 🔹 If we have a premade template (index > 0), ONLY render that template.
        // Other parts (top/bottom/eyes/etc.) are ignored, since the template is a full composite.
        const templateIndex = character?.template ?? 0;
        const hasTemplate = templateIndex > 0;

        if (hasTemplate) {
            const url = urlForPart("template", templateIndex);
            return url ? [url] : [];
        }

        const parts = PART_ORDER(g, fullbodyChosen).map((part) => {
            if (part === "fullbody") {
                return urlForPart("fullbody", character?.fullbody ?? 0);
            }
            if (part === "top" || part === "bottom") {
                if (fullbodyChosen) return ""; // hidden if fullbody is used
                return urlForPart(part, character?.[part] ?? 0);
            }
            return urlForPart(part, character?.[part] ?? 0);
        });

        return parts.filter(Boolean);
    } catch {
        return [];
    }
}


async function rasterizeCharacter(character) {
    const urls = characterLayerUrls(character);
    const imgs = (await Promise.all(urls.map(loadImg))).filter(Boolean);
    const c = document.createElement("canvas");
    c.width = CHAR_W;
    c.height = CHAR_H;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, CHAR_W, CHAR_H);
    imgs.forEach((im) => ctx.drawImage(im, 0, 0, CHAR_W, CHAR_H));
    return c.toDataURL("image/png");
}

function resolveSpeakerCharacter(line, charactersById, charactersByName) {
    const raw = String(line?.speaker || "").trim();
    if (!raw || /^(narrator)$/i.test(raw)) return null;
    return charactersById.get(raw) || charactersByName.get(raw) || null;
}

function getSceneChoices(scene) {
    if (!scene) return [];
    if (scene.mode === "continue") {
        const cid = scene?.continue?.childId;
        return cid ? [{ text: scene?.continue?.text || t`Continue`, childId: cid }] : [];
    }
    if (scene.mode === "choices") {
        const arr = Array.isArray(scene?.choices) ? scene.choices : [];
        return arr
            .map((c, i) => ({
                text: (c?.text || "").trim() || t`Option ${i + 1}`,
                childId: String(c?.childId || ""),
            }))
            .filter((c) => c.childId);
    }
    return [];
}

function getNextRenderableIndex(lines, fromIdx) {
    const L = Array.isArray(lines) ? lines : [];
    for (let i = Math.max(0, fromIdx); i < L.length; i++) {
        const s = (L[i]?.text || "").trim();
        if (s.length) return i;
    }
    return -1;
}

/* ────────────────────────────── main component ─────────────────────────────── */
export default function InteractiveChapterPreview({
    storyID,
    chapterID,             // Firestore doc id STRING (matches your CF)
    onClose,               // optional
    height = "100vh",      // fit screen height by default
}) {
    const theme = useTheme();
    const containerRef = React.useRef(null);
    const imgRef = React.useRef(null);

    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState("");

    const [chapter, setChapter] = React.useState(null);
    const [scenesById, setScenesById] = React.useState(new Map());

    const [charactersById, setCharactersById] = React.useState(new Map());
    const [charactersByName, setCharactersByName] = React.useState(new Map());
    const [characterSprites, setCharacterSprites] = React.useState(new Map()); // id -> dataURL

    const [sceneId, setSceneId] = React.useState(null);
    const [lineIdx, setLineIdx] = React.useState(0);

    // background info
    const [bgUrl, setBgUrl] = React.useState("");
    const [bgNatural, setBgNatural] = React.useState({ w: 0, h: 0 }); // intrinsic
    const [stageSize, setStageSize] = React.useState({ w: 0, h: 0 });  // rendered stage px

    // preload manifest + chapter + characters
    React.useEffect(() => {
        let alive = true;
        (async () => {
            try {
                setLoading(true);
                setError("");
                await ensureAssetsManifest();

                const [chapRes, chars] = await Promise.all([
                    getInteractiveStoryChapter(storyID, chapterID),
                    getCharacters(storyID),
                ]);

                if (!alive) return;

                const chap = chapRes?.chapter || chapRes; // support either shape
                const sbid = new Map((chap?.scenes || []).map((s) => [String(s.id), s]));
                const start = String(chap?.startSceneId || "");
                const end = String(chap?.endSceneId || "");
                setChapter({ ...chap, startSceneId: start, endSceneId: end });
                setScenesById(sbid);
                setSceneId(start || (chap?.scenes?.[0]?.id ? String(chap.scenes[0].id) : null));

                // characters maps
                const byId = new Map();
                const byName = new Map();
                (Array.isArray(chars) ? chars : []).forEach((c) => {
                    const id = String(c.id ?? c.characterId ?? "");
                    if (!id) return;
                    byId.set(id, c);
                    if (c.name) byName.set(String(c.name), c);
                });
                setCharactersById(byId);
                setCharactersByName(byName);

                // pre-render sprites (best-effort)
                const spritePairs = [];
                for (const [cid, cobj] of byId.entries()) {
                    try {
                        const url = await rasterizeCharacter(cobj);
                        spritePairs.push([cid, url]);
                    } catch { /* ignore */ }
                }
                if (alive) setCharacterSprites(new Map(spritePairs));
            } catch (e) {
                if (alive) setError(e?.message || t`Failed to load preview.`);
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [storyID, chapterID]);

    const scene = sceneId ? scenesById.get(sceneId) : null;

    // Background: resolve URL & read intrinsic size
    React.useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const url = await resolveBackgroundUrlByIndex(
                    Number.isFinite(scene?.background) ? scene.background : 0
                );
                if (!alive) return;
                setBgUrl(url || "");
                if (url) {
                    const img = await loadImg(url);
                    if (!alive) return;
                    setBgNatural({ w: img?.naturalWidth || img?.width || 0, h: img?.naturalHeight || img?.height || 0 });
                } else {
                    setBgNatural({ w: 0, h: 0 });
                }
            } catch {
                if (alive) {
                    setBgUrl("");
                    setBgNatural({ w: 0, h: 0 });
                }
            }
        })();
        return () => { alive = false; };
    }, [scene?.background]);

    // Current line (skip empties)
    React.useEffect(() => {
        if (!scene) return;
        const idx = getNextRenderableIndex(scene.lines, 0);
        setLineIdx(idx >= 0 ? idx : -1);
    }, [sceneId]);

    const isEnd = !!scene?.lastScene;
    const choices = !isEnd ? getSceneChoices(scene) : [];

    const onNext = React.useCallback(() => {
        if (!scene) return;
        const L = Array.isArray(scene.lines) ? scene.lines : [];
        if (lineIdx < 0) return; // already in choices
        const nextIdx = getNextRenderableIndex(L, lineIdx + 1);
        if (nextIdx >= 0) {
            setLineIdx(nextIdx);
            return;
        }
        // move to choices phase
        setLineIdx(-1);
    }, [scene, lineIdx]);

    const onChoose = React.useCallback((childId) => {
        const tid = String(childId || "");
        if (!tid || !scenesById.has(tid)) return;
        setLineIdx(0);
        setSceneId(tid);
    }, [scenesById]);

    const onRestart = React.useCallback(() => {
        const start = String(chapter?.startSceneId || "");
        if (!start) return;
        setLineIdx(0);
        setSceneId(start);
    }, [chapter]);

    // Active speaker character/sprite
    const activeLine =
        scene && lineIdx >= 0 && Array.isArray(scene.lines) ? scene.lines[lineIdx] : null;
    const activeCharacter =
        activeLine ? resolveSpeakerCharacter(activeLine, charactersById, charactersByName) : null;
    const activeSprite = activeCharacter ? characterSprites.get(String(activeCharacter.id)) : null;
    const activeSpeakerName =
        activeCharacter?.name ||
        (/^(narrator)$/i.test(String(activeLine?.speaker || "")) ? "" : String(activeLine?.speaker || ""));

    /* ────────────── Stage layout: keep BG aspect, fit container height ───────── */
    const recomputeStageSize = React.useCallback(() => {
        const el = containerRef.current;
        if (!el) return;
        const contW = el.clientWidth;
        const contH = el.clientHeight;
        if (!bgNatural.w || !bgNatural.h || !contW || !contH) {
            setStageSize({ w: contW, h: contH });
            return;
        }
        const bgRatio = bgNatural.w / bgNatural.h;

        // Fit by height first (requirement), then cap to container width if overflow
        let h = contH;
        let w = h * bgRatio;
        if (w > contW) {
            // Very narrow device: fall back to width-fit to avoid overflow
            w = contW;
            h = w / bgRatio;
        }
        setStageSize({ w: Math.floor(w), h: Math.floor(h) });
    }, [bgNatural]);

    // Recompute on size changes
    React.useEffect(() => {
        recomputeStageSize();
    }, [recomputeStageSize, bgUrl]);

    React.useEffect(() => {
        if (!containerRef.current) return;
        const ro = new ResizeObserver(() => recomputeStageSize());
        ro.observe(containerRef.current);
        return () => ro.disconnect();
    }, [recomputeStageSize]);

    // Keyboard shortcuts: Space/Enter -> next; 1..9 -> pick choice
    React.useEffect(() => {
        const onKey = (e) => {
            if (loading || error || !scene) return;
            if (e.key === " " || e.key === "Enter") {
                if (lineIdx >= 0) {
                    e.preventDefault();
                    onNext();
                }
            } else if (lineIdx < 0 && !isEnd) {
                const n = Number(e.key);
                if (Number.isInteger(n) && n >= 1 && n <= Math.min(9, choices.length)) {
                    e.preventDefault();
                    onChoose(choices[n - 1].childId);
                }
            }
        };
        window.addEventListener("keydown", onKey, { passive: false });
        return () => window.removeEventListener("keydown", onKey);
    }, [loading, error, scene, lineIdx, isEnd, choices, onNext, onChoose]);

    return (
        <Box
            ref={containerRef}
            sx={{
                position: "relative",
                width: "100%",
                height,                 // e.g. 100vh
                borderRadius: 2,
                overflow: "hidden",
                border: (t) => `1px solid ${t.palette.divider}`,
                bgcolor: "black",
            }}
        >
            {/* Header bar */}
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                    position: "absolute",
                    top: 8,
                    left: 8,
                    right: 8,
                    zIndex: 5,
                    gap: 1,
                    pointerEvents: "none",
                }}
            >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ pointerEvents: "auto" }}>
                    <Chip
                        size="small"
                        color="primary"
                        label={chapter?.title || t`Chapter`}
                    />

                    {scene?.title && <Chip size="small" label={scene.title} />}
                </Stack>

                <Stack direction="row" spacing={1} sx={{ pointerEvents: "auto" }}>
                    <IconButton title={t`Restart`} onClick={onRestart} sx={{ bgcolor: "background.paper" }}>
                        <RestartAltIcon />
                    </IconButton>
                    {onClose && (
                        <IconButton title={t`Close`} onClick={onClose} sx={{ bgcolor: "background.paper" }}>
                            <CloseIcon />
                        </IconButton>
                    )}
                </Stack>
            </Stack>

            {/* Loading / error overlays */}
            {loading && (
                <Box sx={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", zIndex: 6 }}>
                    <CircularProgress />
                </Box>
            )}
            {!!error && !loading && (
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        display: "grid",
                        placeItems: "center",
                        color: "error.main",
                        zIndex: 6,
                        p: 2,
                        textAlign: "center",
                        bgcolor: "rgba(0,0,0,0.4)",
                    }}
                >
                    <Typography variant="body1">{error}</Typography>
                </Box>
            )}

            {/* ──────────────── STAGE: keeps background aspect & overlays ───────────── */}
            <Box
                sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: `${stageSize.w}px`,
                    height: `${stageSize.h}px`,
                    zIndex: 1,
                }}
            >
                {/* Background fills the stage exactly, preserving intrinsic ratio because stage is sized to it */}
                {bgUrl && (
                    <Box
                        ref={imgRef}
                        component="img"
                        alt={t`background`}
                        src={bgUrl}
                        sx={{
                            position: "absolute",
                            inset: 0,
                            width: "100%",
                            height: "100%",
                            objectFit: "cover", // stage already matches bg ratio
                            filter: "saturate(1.05) contrast(1.02)",
                            zIndex: 1,
                            pointerEvents: "none",
                            userSelect: "none",
                        }}
                    />
                )}

                {/* Character sprite (scaled relative to stage height) */}
                {activeSprite && (
                    <Box
                        component="img"
                        src={activeSprite}
                        alt={activeSpeakerName || t`character`}
                        sx={{
                            position: "absolute",
                            bottom: 0,
                            left: { xs: "0%", sm: "0%" }, // % relative to stage width
                            height: { xs: "100%", sm: "100%" }, // % of stage height
                            maxHeight: "100%",
                            objectFit: "contain",
                            zIndex: 2,
                            filter: "drop-shadow(0px 4px 8px rgba(0,0,0,0.6))",
                            pointerEvents: "none",
                            userSelect: "none",
                        }}
                    />
                )}

                {/* Choices – fill the background width (stage width) */}
                {!loading && !error && scene && (lineIdx < 0 || (Array.isArray(scene.lines) && scene.lines.length === 0)) && !isEnd && (choices.length > 0) && (
                    <Stack
                        spacing={1}
                        sx={{
                            position: "absolute",
                            left: 0,
                            right: 0,
                            top: "50%",
                            transform: "translateY(-50%)",
                            zIndex: 3,
                            width: "calc(100% - 32px)", // full stage width, small side padding
                            maxHeight: { xs: "48%", sm: "56%" },
                            overflowY: "auto",
                            mx: "auto",
                            px: 1,
                        }}
                    >
                        {choices.map((c, i) => (
                            <Button
                                key={`${c.childId}-${i}`}
                                variant="contained"
                                fullWidth
                                onClick={() => onChoose(c.childId)}
                                endIcon={<ArrowForwardIcon />}
                                sx={{
                                    justifyContent: "space-between",
                                    alignItems: "flex-start",
                                    textTransform: "none",
                                    borderRadius: 2,
                                    boxShadow: 3,
                                    py: { xs: 1, sm: 1.1 },
                                    px: 1.25,
                                    minHeight: 44,
                                    lineHeight: 1.35,
                                    "& .MuiButton-endIcon": { mt: "2px" },
                                }}
                            >
                                <Typography
                                    component="span"
                                    sx={{
                                        fontSize: 16,
                                        textAlign: "left",
                                        whiteSpace: "normal",
                                        wordBreak: "break-word",
                                        hyphens: "auto",
                                        flex: 1,
                                        pr: 1,
                                    }}
                                >
                                    {c.text || t`Option ${i + 1}`}
                                </Typography>
                            </Button>
                        ))}
                    </Stack>
                )}


                {/* End-of-chapter overlay */}
                {isEnd && !loading && !error && (lineIdx < 0 || !Array.isArray(scene?.lines) || scene.lines.length === 0) && (
                    <Box
                        sx={{
                            position: "absolute",
                            left: "50%",
                            top: "50%",
                            transform: "translate(-50%, -50%)",
                            zIndex: 3,
                            bgcolor: "rgba(0,0,0,0.6)",
                            color: "white",
                            px: 2.5,
                            py: 1.5,
                            borderRadius: 2,
                            border: (t) => `1px solid ${t.palette.divider}`,
                            boxShadow: 4,
                            textAlign: "center",
                        }}
                    >
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                            {t`End of Chapter`}
                        </Typography>
                        <Stack direction="row" spacing={1} justifyContent="center">
                            <Button onClick={onRestart} variant="outlined">
                                {t`Restart`}
                            </Button>
                            {onClose && (
                                <Button onClick={onClose} variant="contained">
                                    {t`Close`}
                                </Button>
                            )}
                        </Stack>

                    </Box>
                )}

                {/* Dialogue box (anchored to stage) */}
                {!loading &&
                    !error &&
                    scene &&
                    lineIdx >= 0 &&
                    Array.isArray(scene.lines) &&
                    scene.lines.length > 0 && (
                        <Box
                            sx={{
                                position: "absolute",
                                left: "50%",
                                bottom: 16,
                                transform: "translateX(-50%)",
                                width: { xs: "94%", sm: "88%" },
                                zIndex: 3,
                                borderRadius: 2,
                                backdropFilter: "blur(6px)",
                                bgcolor: "rgba(20,20,20,0.62)",
                                color: "white",
                                border: "1px solid rgba(255,255,255,0.16)",
                                boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
                            }}
                        >
                            {activeSpeakerName && (
                                <Box sx={{ px: 1.25, pt: 1 }}>
                                    <Chip label={activeSpeakerName} size="small" color="primary" />
                                </Box>
                            )}
                            <Box sx={{ px: 1.5, py: 1.25 }}>
                                <Typography sx={{ fontSize: 16, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
                                    {(scene.lines[lineIdx]?.text || "").trim()}
                                </Typography>
                            </Box>

                            <Stack direction="row" justifyContent="flex-end" sx={{ px: 1.25, pb: 1.25 }}>
                                <Button
                                    variant="contained"
                                    size="small"
                                    onClick={onNext}
                                    endIcon={<ArrowForwardIcon />}
                                    sx={{ textTransform: "none" }}
                                >
                                    {t`Next`}
                                </Button>
                            </Stack>
                        </Box>
                    )}

            </Box>
        </Box>
    );
}
